import React, { useState } from 'react';
import { Box, Drawer, Card, CardContent, Typography, CardHeader, CircularProgress } from '@mui/material';
import api from "../../../../services/api";
import FormAddEditToolbar from "../../../../components/global/AddEditBar";
import { useNavigate } from 'react-router-dom';
import { useNotifier } from "muinotify";
import ExpensesForm from "../ExpensesForm";
import useSWR, { mutate } from 'swr';
import ErrorNotification from '../../../../components/ErrorNotification/ErrorNotification';
import * as yup from 'yup';

const validationSchema = yup.object().shape({
    p_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
    pci_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
    exp_inv_no: yup.string()
        .required('Το πεδίο είναι υποχρεωτικό')
        .matches(/^[\x00-\x7F]*$/, 'Επιτρέπονται μόνο λατινικοί χαρακτήρες και σύμβολα'),
    exp_beneficiary_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
    exp_amount: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
    exp_assigned_episodes: yup
        .array()
        .of(yup.number().required('Κάθε επεισόδιο πρέπει να είναι ακέραιος'))
        .required('Απαιτήται επιλογή επεισοδίων')
        .min(1, 'Πρέπει να επιλεχθεί τουλάχιστον ένα επεισόδιο'),
    // exp_date: yup.nullable()
    //     .max(new Date(), "Date cannot be in the future")
});

const validationSchemaIsPrediction = yup.object().shape({
    p_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
    pci_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
    exp_inv_no: yup.string()
        .matches(/^[\x00-\x7F]*$/, 'Επιτρέπονται μόνο λατινικοί χαρακτήρες και σύμβολα')
        .nullable(true),
    exp_beneficiary_id: yup.number().required('Το πεδίο είναι υποχρεωτικό').positive('Πρέπει να είναι θετικός αριθμός'),
    exp_assigned_episodes: yup
        .array()
        .of(yup.number().required('Κάθε επεισόδιο πρέπει να είναι ακέραιος'))
        .required('Απαιτήται επιλογή επεισοδίων')
        .min(1, 'Πρέπει να επιλεχθεί τουλάχιστον ένα επεισόδιο'),
    // exp_date: yup.nullable()
    //     .max(new Date(), "Date cannot be in the future")
});

const ExpensesAdd = (props) => {
    const navigate = useNavigate();
    const { notifier } = useNotifier();
    const { data: beneficiaries } = useSWR(`/expense-beneficiary`);
    const { data: productions } = useSWR(`/productions`);

    const [state, setState] = useState({
        p_id: null,
        pci_id: null,
        exp_inv_no: null,
        exp_beneficiary_id: 0,
        exp_amount: 0,
        exp_assigned_episodes: [],
        exp_notes: '',
        exp_pred: false,
        exp_date: null,
    });

    const { data: production_category_item, error: productionError } = useSWR(
        state.p_id ? `/production-category-item/of-production/${state.p_id}` : null
    );

    const headerNames = {
        p_id: 'Production ID',
        pci_id: 'PCI ID',
        exp_inv_no: 'Export Invoice Number',
        exp_beneficiary_id: 'Export Beneficiary ID',
        exp_amount: 'Export Amount',
        exp_assigned_episodes: 'Assigned Episodes'
    };

    const URL = `expense`;
    const NAV_PATH = `expenses`;

    const [errorMessage, setErrorMessage] = useState();
    const [errors, setErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);

    function updateField(fieldName, value) {
        setState({ ...state, [fieldName]: value });
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {

            if (state.exp_pred === true || !state.exp_amount) {
                if (!state.exp_amount) {
                    state.exp_amount = 0;
                }
                await validationSchemaIsPrediction.validate(state, { abortEarly: false });

            } else {
                await validationSchema.validate(state, { abortEarly: false });
            }

            if (state.exp_date) {
                state.exp_date = new Date(state.exp_date);
                try {
                    state.exp_date.toISOString();
                } catch (error) {
                    setErrorMessage(error);
                    notifier(<ErrorNotification error={error} />, { variant: "error" });
                    return;
                }
            }

            setErrors({});

            // Prepare FormData for multipart/form-data
            const formData = new FormData();
            if (state.file) {
                formData.append("file", state.file, encodeURIComponent(state.file.name));
            }
            formData.append("p_id", state.p_id);
            formData.append("pci_id", state.pci_id);
            formData.append("exp_inv_no", state.exp_inv_no);
            formData.append("exp_beneficiary_id", state.exp_beneficiary_id);
            formData.append("exp_amount", state.exp_amount);
            formData.append("exp_notes", state.exp_notes);
            formData.append("exp_pred", state.exp_pred ? "true" : "false");
            state.exp_assigned_episodes.forEach((episode) =>
                formData.append("exp_assigned_episodes[]", episode)
            );
            formData.append("exp_date", state.exp_date ? state.exp_date.toISOString() : null);

            setIsUpdating(true);
            api.post(`/${URL}/`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
                .then((response) => {
                    mutate(`/${NAV_PATH}`);
                    notifier("New expense record added.", { variant: "success" });
                    setDrawerOpen(false);
                    navigate(-1);
                })
                .catch((error) => {
                    setIsUpdating(false);
                    setErrorMessage(error);
                    notifier(<ErrorNotification error={error} />, { variant: "error" });
                })

        } catch (validationErrors) {
            const formattedErrors = {};
            validationErrors.inner.forEach((error) => {
                formattedErrors[error.path] = error.message;
            });
            setErrors(formattedErrors);
        }
    };


    const handleSave = () => {
        const form = document.getElementById(`${URL}-form`);
        if (form) {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
    };

    const handleCancel = () => {
        setState({
            p_id: 0,
            pci_id: 0,
            exp_inv_no: '',
            exp_beneficiary_id: 0,
            exp_amount: 0,
            exp_assigned_episodes: [],
            file: null,
        });
        navigate(-1);
    };


    const [drawerOpen, setDrawerOpen] = useState(true);

    return (
        <>
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleCancel}
                PaperProps={{
                    style: {
                        width: '100%',
                        height: '100vh',
                        maxWidth: '800px',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <Card
                    sx={{
                        width: '100%',
                        maxWidth: 800,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {/* Header Section */}
                    <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                        <CardHeader
                            title={<Typography variant="h3" gutterBottom={false}>Προσθήκη Εξόδου</Typography>}
                            sx={{
                                padding: '8px 16px', // Adjust padding to your design
                            }}
                        />
                    </Box>

                    {/* Scrollable Content Section */}
                    <CardContent
                        sx={{
                            display: 'flex',
                            height: '100vh',
                            flexGrow: 1, // Allow this section to grow and shrink
                            overflowY: 'auto',
                            margin: 'initial',
                        }}
                    >
                        <form id={`${URL}-form`} onSubmit={handleSubmit}>
                            <ExpensesForm
                                state={state}
                                errors={errors}
                                updateField={updateField}
                                headerNames={headerNames}
                                beneficiaries={beneficiaries}
                                productions={productions}
                                production_category_item={production_category_item}
                            />
                        </form>
                        {isUpdating && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                    zIndex: 1000,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}
                    </CardContent>

                    {/* Toolbar (Footer Section) */}
                    <Box >
                        <FormAddEditToolbar
                            handleSave={handleSave}
                            handleCancel={handleCancel}
                        />
                    </Box>
                </Card>
            </Drawer >
        </>
    );
};

export default ExpensesAdd;
